import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useState } from "react";
import { LOCALES, resolveLocale, type Locale } from "@shared/types/locale.ts";
import i18n from "../../i18n";
import { fetchUserLocale, saveUserLocale } from "../../lib/user_profile_api";
import { useUserContext } from "../UserContext";

interface LocaleState {
  /** The locale of the interface and of the explanations the person reads. */
  locale: Locale;
  /** Switches the locale, and keeps it on the account when the person has one. */
  setLocale: (locale: Locale) => Promise<void>;
}

const LocaleContext = createContext<LocaleState | null>(null);

/** The locale of the browser, as one we are available in. */
function getBrowserLocale(): Locale {
  return resolveLocale(navigator.language);
}

/**
 * Owns the person's locale. A visitor gets the browser's. A signed-in person gets the one stored on
 * their profile; an account with none yet takes the browser's, saved there as it is first seen.
 * Whatever the locale, the interface language follows it.
 */
export function LocaleProvider({ children }: PropsWithChildren) {
  const { session } = useUserContext();
  const accountId = session && !session.user.is_anonymous ? session.user.id : null;
  const [locale, setLocaleState] = useState<Locale>(getBrowserLocale);

  useEffect(() => {
    void i18n.changeLanguage(LOCALES[locale].language);
    document.documentElement.lang = LOCALES[locale].language;
  }, [locale]);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    void (async () => {
      const { locale: stored } = await fetchUserLocale();
      if (cancelled) return;
      if (stored) setLocaleState(stored);
      else await saveUserLocale(getBrowserLocale());
    })().catch(() => {
      // The profile's locale is a convenience: the browser's locale stays in use.
    });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const setLocale = useCallback(
    async (next: Locale) => {
      setLocaleState(next);
      if (accountId) await saveUserLocale(next);
    },
    [accountId]
  );

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/** The person's locale and how to change it. */
export function useLocale(): LocaleState {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used inside LocaleProvider");
  return ctx;
}
